import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Clock, Download } from "lucide-react";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

// Add { available:true, href } once a resource is published. Empty items never
// render a download button.
const RESOURCES = [
  { title: "Post-Event Executive Summary", desc: "A concise recap of Summit outcomes and impact.", available: true, to: "/impact", cta: "View Summary" },
  { title: "Event Photos", desc: "Selected photography from the Summit.", available: true, to: "/highlights", cta: "View Photos" },
  { title: "Participant Resources", desc: "Follow-up materials for attendees.", available: false },
  { title: "Supplier Follow-Up", desc: "Guidance for continuing supplier conversations.", available: false },
  { title: "Future Travel + Leisure Supplier Engagement Opportunities", desc: "What's next for the supplier ecosystem.", available: false },
];

export default function PostEventResources() {
  const { user } = useAuth();
  return (
    <Layout>
      <section className="bg-[#0A0A0A] text-white" data-testid="resources-hero">
        <div className="container-summit py-20 md:py-28">
          <motion.div {...fade}>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#C9B792]">Post-Event Resources</div>
            <h1 className="serif text-4xl sm:text-6xl mt-6 leading-tight">Resources & follow-up.</h1>
            <p className="mt-6 text-white/60 max-w-2xl leading-relaxed">
              Materials from the Travel + Leisure Strategic Supplier Summit will be published here as they become
              available. Registered participants can also access the full portal anytime.
            </p>
            {!user && <Link to="/login" className="btn-gold mt-8 inline-flex" data-testid="resources-signin">Sign in to your portal <ArrowRight size={15} /></Link>}
          </motion.div>
        </div>
      </section>

      <section className="container-summit py-16 md:py-24" data-testid="resources-list">
        <div className="grid gap-px bg-zinc-200 border border-zinc-200">
          {RESOURCES.map((r) => (
            <div key={r.title} className="bg-white p-6 md:p-8 flex items-center justify-between gap-6" data-testid={`resource-${r.title.toLowerCase().replace(/[^a-z]+/g,"-").slice(0,24)}`}>
              <div>
                <h3 className="serif text-xl md:text-2xl">{r.title}</h3>
                <p className="mt-2 text-sm text-zinc-600">{r.desc}</p>
              </div>
              {r.available ? (
                r.to ? (
                  <Link to={r.to} className="btn-black shrink-0" data-testid={`resource-cta-${r.to.replace('/','')}`}>{r.cta || "View"} <ArrowRight size={15} /></Link>
                ) : (
                  <a href={r.href} className="btn-black shrink-0" download><Download size={15} /> Download</a>
                )
              ) : (
                <span className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400 border border-zinc-200 px-4 py-2">
                  <Clock size={13} /> Coming Soon
                </span>
              )}
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
